import { useState } from 'react';
import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import {
  downloadInternationalRemoteRoster,
  getInternationalRemoteRoster,
  type RosterEmployee,
} from '../api/orgs.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';
import { formatDate } from '../lib/format.js';

/**
 * /international-remote-roster — ADR 0010 Phase 4.
 *
 * Lists the tenant's international_remote employees (excluded from country
 * statutory filings). owner/admin/hr; finance/employee get a 403 from the API
 * and see an access message (the nav has no per-item role gating in this
 * codebase, so the link shows to all staff — the API is the authority).
 */
export const internationalRemoteRosterRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/international-remote-roster',
  component: InternationalRemoteRosterPage,
});

function formatWorkArrangement(t: string | null): string {
  switch (t) {
    case 'full_time':
      return 'Full-time';
    case 'part_time':
      return 'Part-time';
    case 'contract':
      return 'Contract';
    case 'intern':
      return 'Intern';
    default:
      return '—';
  }
}

function InternationalRemoteRosterPage(): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['international-remote-roster'],
    queryFn: getInternationalRemoteRoster,
    retry: false,
  });

  return (
    <section className="p-8 space-y-6">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">International Remote Workers</h1>
        <p className="text-sm text-gray-400 max-w-2xl">
          Employees on the International remote payroll model. These employees
          are excluded from country statutory filings (per ADR 0010).
        </p>
      </header>

      {isLoading ? (
        <div className="rounded border border-gray-900 p-8 text-center text-gray-500">
          Loading roster…
        </div>
      ) : null}

      {isError ? (
        <Alert variant="error" title="Could not load the roster">
          {error instanceof ApiError && error.status === 403
            ? "You don't have permission to view the international remote roster."
            : error instanceof Error
              ? error.message
              : 'Unknown error'}
        </Alert>
      ) : null}

      {data ? (
        <>
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-gray-400">
              <span className="text-white font-medium">{data.totalCount}</span>{' '}
              international remote {data.totalCount === 1 ? 'employee' : 'employees'} in{' '}
              {data.organizationName}. Primary country:{' '}
              <span className="font-mono">{data.primaryCountry}</span>.
            </p>
            <button
              type="button"
              disabled={busy || data.totalCount === 0}
              onClick={async () => {
                setDownloadError(null);
                setBusy(true);
                try {
                  await downloadInternationalRemoteRoster(data.organizationName);
                } catch (caught) {
                  setDownloadError(
                    caught instanceof ApiError ? caught.message : 'Download failed. Try again.',
                  );
                } finally {
                  setBusy(false);
                }
              }}
              className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
            >
              {busy ? 'Downloading…' : 'Download CSV'}
            </button>
          </div>

          {downloadError ? <Alert variant="error">{downloadError}</Alert> : null}

          {data.totalCount === 0 ? (
            <div className="rounded border border-dashed border-gray-800 p-10 text-center text-sm text-gray-400">
              No international remote employees in {data.organizationName}.
            </div>
          ) : (
            <RosterTable employees={data.employees} />
          )}
        </>
      ) : null}
    </section>
  );
}

function RosterTable({ employees }: { employees: RosterEmployee[] }): JSX.Element {
  return (
    <div className="rounded border border-gray-900 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-950 text-left text-gray-400 uppercase text-xs">
          <tr>
            <th className="px-4 py-3 font-medium">Employee #</th>
            <th className="px-4 py-3 font-medium">Name</th>
            <th className="px-4 py-3 font-medium">Country</th>
            <th className="px-4 py-3 font-medium">Hire date</th>
            <th className="px-4 py-3 font-medium">Work arrangement</th>
            <th className="px-4 py-3 font-medium">Created</th>
          </tr>
        </thead>
        <tbody>
          {employees.map((e) => (
            <tr key={e.employeeNumber} className="border-t border-gray-900">
              <td className="px-4 py-3 font-mono text-xs">{e.employeeNumber}</td>
              <td className="px-4 py-3">
                {e.lastName}, {e.firstName}
              </td>
              <td className="px-4 py-3 text-gray-400">{e.country}</td>
              <td className="px-4 py-3 text-gray-400">{formatDate(e.hireDate)}</td>
              <td className="px-4 py-3 text-gray-400">{formatWorkArrangement(e.employmentType)}</td>
              <td className="px-4 py-3 text-gray-400">{formatDate(e.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
